"use client";

import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

interface RechartLineComponentProps {
  data: Array<{ label: string } & Record<string, number>>; // label sempre string, outros campos numéricos
  title?: string;
  height?: number;
  color?: string;
  yAxisFormatter?: (value: number) => string;
  valueLabel?: string; // chave do valor
  legendLabel?: string; // legenda exibida na legenda/tooltip
}

const DEFAULT_COLOR = "#22c55e"; // Tailwind green-500
import { useAuth } from "@/context/AuthContext";
import { formatCurrency as fmtCurrency } from "@/utils/formatCurrency";

const RechartLineComponent: React.FC<RechartLineComponentProps> = ({
  data,
  title,
  height = 320,
  color = DEFAULT_COLOR,
  yAxisFormatter = (v) => v.toLocaleString("pt-BR"),
  valueLabel = "value",
  legendLabel,
}) => (
  <div style={{ width: "100%", height }}>
    {!data || data.length === 0 ? (
      <div style={{ padding: 16 }} className="text-sm text-gray-500">
        Nenhum dado para exibir
      </div>
    ) : (
      <>
        {title && <div className="font-semibold mb-2 text-lg">{title}</div>}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="4 4"
              stroke="#e6e6e6"
              vertical={true}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12 }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={yAxisFormatter}
              domain={(() => {
                const values = data.map((d: any) => Number(d[valueLabel]) || 0);
                const max = values.length ? Math.max(...values) : 0;
                const upper = Math.ceil(max * 1.15);
                return [0, Math.max(100, upper)];
              })()}
            />
            <Tooltip
              formatter={(value: any) =>
                value != null ? Number(value).toLocaleString("pt-BR") : value
              }
            />
            <Area
              type="monotone"
              dataKey={valueLabel}
              name={
                legendLabel || (valueLabel === "value" ? "Valor" : valueLabel)
              }
              stroke={color}
              strokeWidth={3}
              fill={color}
              fillOpacity={0.16}
              dot={false}
            />
            <Legend verticalAlign="bottom" align="center" />
          </AreaChart>
        </ResponsiveContainer>
      </>
    )}
  </div>
);

export default RechartLineComponent;
